import React from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DutyHourTypeWithDetails } from '../types';

import { convertDataForBackend } from './utils/convertDataForBackend';
import { schema } from './utils/schema';

import { ADMIN_SETTINGS_DUTY_HOUR_TYPES } from 'RestAPIs/endpoints';
import { useSaveResource } from 'RestAPIs/useSaveResource';

import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/material';

import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';

import { AbsenceSection } from './Sections/AbsenceSection';
import { DecentralizedDutyCodesSection } from './Sections/DecentralizedDutyCodesSection';
import { DefaultSection } from './Sections/DefaultSection';
import { EmployeePortalSection } from './Sections/EmployeePortalSection';
import { ExchangeSection } from './Sections/ExchangeSection';
import { RosterPlanSection } from './Sections/RosterPlanSection';
import { RosterplanRestrictedSection } from './Sections/RosterplanRestrictedSection';
import { GeneralSettingsSection } from './Sections/TimeBoundValuesSection/GeneralSettingsSection';
import { UnlinkAutomaticallySection } from './Sections/UnlinkAutomaticallySection/UnlinkAutomaticallySection';
import { KidsPlanSection } from './Sections/KidsPlanSection';
import { useSelector } from 'hooks';

interface Props {
  handleClose: () => void;
  rowData?: DutyHourTypeWithDetails;
}

export const CreateEditDutyHourTypesForm: React.FC<Props> = ({
  rowData,
  handleClose
}) => {
  const { t } = useTranslation();
  const { hasKidsPlan } = useSelector((state) => state.grants);

  const methods = useForm({
    defaultValues: schema.cast(rowData),
    resolver: yupResolver(schema)
  });

  const { mutateAsync: saveForm, isLoading } = useSaveResource({
    url: ADMIN_SETTINGS_DUTY_HOUR_TYPES,
    id: rowData?.id,
    invalidateQueryKey: ['dutyHourTypes', 'list']
  });

  const onSubmit = async (data) => {
    await saveForm(convertDataForBackend(data)).then(handleClose);
  };

  return (
    <MultiStepForm onSubmit={methods.handleSubmit(onSubmit)} methods={methods}>
      <DefaultSection />
      <GeneralSettingsSection />
      <RosterplanRestrictedSection />
      {hasKidsPlan && <KidsPlanSection />}
      <EmployeePortalSection />
      <ExchangeSection />
      <DecentralizedDutyCodesSection />
      <RosterPlanSection />
      <UnlinkAutomaticallySection />
      <AbsenceSection />
      <MultiStepForm.Actions>
        <Box display="flex" ml="auto">
          <LoadingButton
            loading={isLoading}
            type="submit"
            variant="contained"
            autoFocus
            color="primary"
          >
            {t('save')}
          </LoadingButton>
        </Box>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
};
